import React, {useState, useEffect, useCallback, useRef} from 'react'
import PropTypes from 'prop-types'
import {useLocation, useHistory} from 'react-router-dom'
import {useForm} from 'react-hook-form'
import {
    Skeleton,
    Box,
    Flex,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {CookieStorage} from '@salesforce/commerce-sdk-react/auth/storage'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useProduct from '../../hooks/use-product'
import {Helmet} from 'react-helmet'
import {useIntl} from 'react-intl'
import {isHydrated} from '@salesforce/retail-react-app/app/utils/utils'

import ProductDetailsAccordion from '../../components/product-view/partials/product-details-accordion'
import ThumbnailImages from './partials/thumbnail-images'
import CardImage from './partials/card-image'
import CardTitle from './partials/card-title'
import CardsPriceRange from './partials/cards-price-range'
import CardSelectedDesign from './partials/card-selected-design'
import CardSelectedAmount from './partials/card-selected-amount'
import CardAmountSwatches from './partials/card-amount-swatches'
import CardForm from './partials/card-form'
import CardCheckBalance from './partials/card-check-balance'
import LoadingSpinner from '../../components/loading-spinner'
import {useShopperBasketsMutation} from '@salesforce/commerce-sdk-react'

import {useToast} from '../../hooks/use-toast'
import {useAddToCartDrawerContext} from '../../hooks/use-add-to-cart-drawer'

import {
    useAccessToken,
    useCommerceApi,
    useCustomerBaskets,
    useCustomerId,
    useCustomerOrders,
    useCustomerType
} from '@salesforce/commerce-sdk-react'
import {onClient} from '@salesforce/commerce-sdk-react/utils'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import useTagManager from '../../hooks/use-tag-manager'
import useMonetate from '../../hooks/use-monetate'
import {getTotalQuantity} from '../../utils/basket-utils'
import {useGlobaleContext} from '../../contexts/globale'
import {useQueryClient} from '@tanstack/react-query'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useWishList} from '../../hooks/use-wish-list'
import {useSearchParams} from '../../hooks'
import OptiversalLinks from '../product-detail/partials/optiversal-links'
import {PAYMENT_METHODS} from '../../api/utils/constants'
import logger from '@salesforce/retail-react-app/app/utils/logger-instance'
import {logBasketErrorToSsr} from '../../utils/log-basket-error-to-ssr'

const getGiftCardSchema = (product, selectedAmount, currencyCode, pageUrl) => {
    if (!product) return {}

    const schema = {
        '@context': 'https://schema.org',
        '@type': 'Product',
        '@id': pageUrl ? `${pageUrl}#product` : undefined,
        url: pageUrl,
        name: product?.name,
        sku: product?.id,
        mpn: product?.id,
        image:
            product?.image?.disBaseLink || product?.image?.link
                ? [product?.image?.disBaseLink || product?.image?.link]
                : undefined,
        offers: {
            '@type': 'Offer',
            url: pageUrl,
            priceCurrency: currencyCode || product?.currency,
            price: selectedAmount ? Number(selectedAmount).toFixed(2) : undefined,
            availability: 'https://schema.org/InStock',
            itemCondition: 'https://schema.org/NewCondition',
            hasMerchantReturnPolicy: {
                '@type': 'MerchantReturnPolicy',
                returnPolicyCategory: 'https://schema.org/MerchantReturnNotPermitted'
            }
        },
        hasMerchantReturnPolicy: {
            '@type': 'MerchantReturnPolicy',
            returnPolicyCategory: 'https://schema.org/MerchantReturnNotPermitted'
        }
    }

    if (!schema.offers.priceCurrency) {
        delete schema.offers.priceCurrency
    }
    if (!schema.offers.price) {
        delete schema.offers.price
    }

    return schema
}

function isSafeReturnUrl(url) {
    if (!url || typeof url !== 'string') return false
    try {
        const parsed = new URL(url, window.location.origin)
        return parsed.origin === window.location.origin
    } catch {
        return false
    }
}

function buildFormDataFromBasketItem(item) {
    const toParts = (item.recipientName || '').split(' ')
    const fromParts = (item.senderName || '').split(' ')
    return {
        toFirstName: toParts.slice(0, -1).join(' '),
        toLastName: toParts[toParts.length - 1] ?? '',
        fromFirstName: fromParts.slice(0, -1).join(' '),
        fromLastName: fromParts[fromParts.length - 1] ?? '',
        email: item.recipientEmail || '',
        confirmEmail: item.recipientEmail || '',
        amount: item.amount,
        selectedId: item.c_productID || '',
        message: item.message || ''
    }
}

const GiftCard = () => {
    const location = useLocation()
    const history = useHistory()
    const intl = useIntl()
    const editLineItemId =
        new URLSearchParams(location.search).get('GiftCertificateLineItemID') || null
    const api = useCommerceApi()
    const {clientConfig} = api.shopperExperience
    const {getTokenWhenReady} = useAccessToken()
    const {site} = useMultiSite()
    const queryClient = useQueryClient()
    const form = useForm()

    const {
        app: {commerceAPI}
    } = getConfig()
    const {eGiftCards} = commerceAPI.storefront || {}

    // Default config fallback
    const defaultConfigs = {
        amounts: [25, 50, 75, 100, 150, 200],
        products: {}
    }

    const parseConfigs = () => {
        if (!eGiftCards) return defaultConfigs
        try {
            const parsed = JSON.parse(eGiftCards)
            return parsed.amounts && parsed.products ? parsed : defaultConfigs
        } catch (err) {
            console.warn('Error parsing eGiftCards JSON:', err.message)
            return defaultConfigs
        }
    }

    const clearForm = () => {
        form.setValue('fromFirstName', '')
        form.setValue('fromLastName', '')
        form.setValue('toFirstName', '')
        form.setValue('toLastName', '')
        form.setValue('email', '')
        form.setValue('confirmEmail', '')
        form.setValue('personalizedMessage', '')
    }

    const configs = parseConfigs()
    const customerId = useCustomerId()
    const {isRegistered, customerType} = useCustomerType()
    let productIds = ['15061', '15071', '15091', '15101', '15081']
    try {
        productIds = configs?.sequence?.split(',') || Object.keys(configs.products)
    } catch (e) {
        console.warn('Error occurred in parsing configs.sequence', e.message)
    }

    const [selectedId, setSelectedId] = useState(productIds.length ? productIds[0] : '')
    const [fetchProduct, setFetchProduct] = useState(true)
    const [selectedAmount, setSelectedAmount] = useState(configs.amounts[1] || 50)
    const [showAddToCartLoader, setShowAddToCartLoader] = useState(false)
    const [showAddedCta, setShowAddedCta] = useState(false)

    const {countryISO, currencyCode} = useGlobaleContext()
    const styles = useMultiStyleConfig('GiftCertificate')
    const showToast = useToast()
    const removePaymentInstrumentFromBasket = useShopperBasketsMutation(
        'removePaymentInstrumentFromBasket'
    )

    const {
        isOpen: isDrawerOpen,
        onOpen: openDrawer,
        onClose: closeDrawer
    } = useAddToCartDrawerContext()

    // Close add-to-cart drawer on navigation
    useEffect(() => {
        if (isDrawerOpen) {
            closeDrawer()
        }
        // Don't clear form when in edit mode (pre-fill sets values from sessionStorage)
        if (!editLineItemId) {
            clearForm()
        }
        setDataLayerLoaded(false)
        setGlobalDataLayerLoaded(false)
    }, [location.pathname])

    // Fetch customer baskets and create a basket if none exists
    const {data: baskets, isLoading} = useCustomerBaskets(
        {parameters: {customerId}},
        {enabled: !!customerId, keepPreviousData: true}
    )

    // Fetch product data when selectedId changes
    const {data: giftCard, isLoading: isGcLoading} = useProduct(
        {
            id: selectedId,
            expand: ['availability', 'images', 'prices']
        },
        {enabled: fetchProduct, keepPreviousData: true}
    )

    const product = giftCard?.product

    useEffect(() => {
        if (selectedId) {
            setFetchProduct(true)
        }
    }, [selectedId])

    // Toast helper
    const notify = useCallback(
        (status, message) => showToast({title: message, status}),
        [showToast]
    )

    const removeGiftCertificatePayments = async (basket) => {
        basket?.paymentInstruments?.forEach(async (pi) => {
            if (pi.paymentMethodId === PAYMENT_METHODS.GIFT_CERTIFICATE) {
                await removePaymentInstrumentFromBasket.mutate({
                    parameters: {
                        basketId: basket.basketId,
                        paymentInstrumentId: pi.paymentInstrumentId
                    }
                })
            }
        })
    }

    // Submit form (add gift card to basket)
    const submitForm = async (formValues) => {
        form.clearErrors()
        const {
            confirmEmail,
            email,
            fromFirstName,
            fromLastName,
            personalizedMessage,
            toFirstName,
            toLastName
        } = formValues

        if (confirmEmail !== email) {
            return notify(
                'error',
                intl.formatMessage({
                    id: 'form.match.emails',
                    defaultMessage: 'Please check that the e-mail addresses match and try again.'
                })
            )
        }

        setShowAddToCartLoader(true)
        const token = await getTokenWhenReady()

        try {
            let basketId = baskets?.baskets?.[0]?.basketId
            if (!basketId) {
                const basketsResponse = await fetch(
                    `${clientConfig.proxy}/checkout/shopper-baskets/v1/organizations/${clientConfig.parameters.organizationId}/baskets?siteId=${site?.id}`,
                    {
                        method: 'POST',
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({})
                    }
                )
                const basketsJsonResponse = await basketsResponse.json()
                if (!basketsResponse.ok) {
                    logger.error('ATCXL Error creating basket for gift certificate', {
                        namespace: 'GiftCard.submitForm',
                        additionalProperties: {
                            basketsJsonResponse,
                            customerType,
                            customerId,
                            productId: selectedId
                        }
                    })
                    logBasketErrorToSsr('GiftCard.submitForm.createBasket', {
                        basketsJsonResponse,
                        customerType,
                        customerId,
                        productId: selectedId
                    })
                    throw new Error(basketsJsonResponse?.detail || 'Failed to create basket')
                }
                basketId = basketsJsonResponse?.basketId
            }
            const giftCertBody = {
                amount: selectedAmount,
                c_fnameFrom: fromFirstName,
                c_lnameFrom: fromLastName,
                c_fnameTo: toFirstName,
                c_lnameTo: toLastName,
                message: personalizedMessage,
                recipientEmail: email,
                recipientName: `${toFirstName} ${toLastName}`,
                senderName: `${fromFirstName} ${fromLastName}`,
                c_largeImageURL: configs.products[selectedId],
                c_productID: selectedId,
                c_sku: product?.manufacturerSku,
                c_orderHandleType: product?.c_orderHandleType,
                c_type: productIds.indexOf(selectedId)
            }
            const giftItemResponse = editLineItemId
                ? await api.shopperBaskets.updateGiftCertificateItemInBasket({
                      body: giftCertBody,
                      parameters: {
                          basketId,
                          giftCertificateItemId: editLineItemId
                      },
                      headers: {Authorization: `Bearer ${token}`}
                  })
                : await api.shopperBaskets.addGiftCertificateItemToBasket({
                      body: giftCertBody,
                      parameters: {basketId},
                      headers: {Authorization: `Bearer ${token}`}
                  })
            if (giftItemResponse?.giftCertificateItems?.length) {
                await removeGiftCertificatePayments(basket)
                tagManager.addToCart(
                    product,
                    product,
                    null,
                    null,
                    'other',
                    null,
                    false,
                    null,
                    null,
                    true
                )
                tagManager.addToCartGA4({
                    itemsAdded: [{product: {...product, price: selectedAmount}}],
                    variant: product,
                    method: 'gift certificate',
                    currency: currencyCode
                })
                setShowAddedCta(true)
                setTimeout(async () => {
                    await queryClient.invalidateQueries()
                    setShowAddToCartLoader(false)
                    monetate.monetateTrackPage(
                        'addToBagModal',
                        window.mtBreadcrumb,
                        giftItemResponse?.productItems,
                        customer?.customerType === 'registered',
                        wishlist?.customerProductListItems?.length || 0,
                        null,
                        null,
                        [{masterId: product?.id, variantId: product?.id}],
                        giftItemResponse?.giftCertificateItems
                    )
                    setTimeout(async () => {
                        setShowAddedCta(false)
                        if (editLineItemId) {
                            notify(
                                'success',
                                intl.formatMessage({
                                    id: 'cart_giftCert.edit.success',
                                    defaultMessage: 'Gift certificate updated.'
                                })
                            )
                            const returnUrl = new URLSearchParams(location.search).get('returnUrl')
                            if (returnUrl && isSafeReturnUrl(returnUrl)) {
                                window.location.href = returnUrl
                            } else {
                                history.push('/cart')
                            }
                            return
                        }
                        const priceData = {
                            currentPrice: selectedAmount,
                            listPrice: selectedAmount,
                            pricePerUnit: selectedAmount,
                            isOnSale: false,
                            isASet: false,
                            isMaster: false,
                            isRange: false,
                            tieredPrice: selectedAmount,
                            maxPrice: selectedAmount
                        }

                        openDrawer({
                            product,
                            itemsAdded: product,
                            selectedQuantity: 1,
                            priceData,
                            heroImage: configs.products[selectedId]
                        })
                        clearForm()
                    }, 1000)
                }, 2000)
            }
        } catch (err) {
            setShowAddToCartLoader(false)
            const errorResponse = err?.response ? await err.response.json() : null
            logger.error('ATCXL Error adding gift certificate to basket', {
                namespace: 'GiftCard.submitForm',
                additionalProperties: {
                    err,
                    errorResponse,
                    basketId: baskets?.baskets?.[0]?.basketId,
                    customerType,
                    customerId,
                    productId: selectedId
                }
            })
            logBasketErrorToSsr('GiftCard.submitForm', {
                err,
                errorResponse,
                basketId: baskets?.baskets?.[0]?.basketId,
                customerType,
                customerId,
                productId: selectedId
            })
            notify(
                'error',
                errorResponse?.statusMessage ||
                    intl.formatMessage({
                        id: 'cart.add.error',
                        defaultMessage: 'Failed to add item to cart.'
                    })
            )
        }
    }

    // Cookie helper (client-side)
    const cookie =
        onClient() &&
        new CookieStorage({
            keySuffix: '',
            sharedContext: !onClient()
        })

    const {isLoading: isBasketLoading, data: basket} = useCurrentBasket()
    const {data: wishlist, isLoading: isWishlistLoading} = useWishList()

    // Edit mode: pre-fill form from basket (cart data loaded via useCurrentBasket)
    const editLineItemPreFilled = useRef(false)
    const lastPrefilledEditLineItemId = useRef(null)
    useEffect(() => {
        if (!editLineItemId || !basket?.giftCertificateItems) return
        if (typeof window === 'undefined') return
        const lineItem = basket.giftCertificateItems.find(
            (i) => i?.giftCertificateItemId === editLineItemId || i?.uuid === editLineItemId
        )
        if (!lineItem) return
        if (
            lastPrefilledEditLineItemId.current === editLineItemId &&
            editLineItemPreFilled.current
        ) {
            return
        }
        editLineItemPreFilled.current = true
        lastPrefilledEditLineItemId.current = editLineItemId
        const data = buildFormDataFromBasketItem(lineItem)
        const amount = Number(data.amount) || configs.amounts[1]
        const selectedIdForForm =
            data.selectedId && productIds.includes(data.selectedId)
                ? data.selectedId
                : productIds[0]
        form.reset(
            {
                toFirstName: data.toFirstName ?? '',
                toLastName: data.toLastName ?? '',
                fromFirstName: data.fromFirstName ?? '',
                fromLastName: data.fromLastName ?? '',
                email: data.email ?? '',
                confirmEmail: data.confirmEmail ?? '',
                personalizedMessage: data.message ?? ''
            },
            {keepDefaultValues: false}
        )
        setSelectedAmount(amount)
        if (selectedIdForForm) setSelectedId(selectedIdForForm)
    }, [editLineItemId, basket?.giftCertificateItems, configs.amounts, form, productIds])
    const {data: customer} = useCurrentCustomer()
    const [globalDataLoaded, setGlobalDataLayerLoaded] = useState(false)
    const [dataLayerLoaded, setDataLayerLoaded] = useState(false)
    const debounceRef = useRef(null)
    const tagManager = useTagManager()
    const gcRef = useRef()
    const monetate = useMonetate()

    const {changeUp, optiversal} = commerceAPI
    const {configChangeup} = changeUp || {}
    const {configOptiversal} = optiversal || {}
    let changeUpProductID = null
    try {
        const jsonConfig = JSON.parse(configChangeup || '{}')
        changeUpProductID = jsonConfig.enabled ? jsonConfig.productID : null
    } catch (err) {
        console.warn(err.message)
    }

    var enabledRelatedLinksPDP = false
    var maxLinksPDP = 10

    try {
        const jsonConfig = JSON.parse(configOptiversal)
        enabledRelatedLinksPDP = jsonConfig.enabledRelatedLinksPDP
        maxLinksPDP = jsonConfig.maxLinksPDP
    } catch (e) {
        console.warn(e.message)
    }

    const orderSearchParams = useSearchParams({limit: 1, offset: 0})
    const {limit, offset} = orderSearchParams[0]
    const {data: {data: orders} = {}, isLoading: isOrdersLoading} = useCustomerOrders(
        {
            parameters: {customerId, limit, offset}
        },
        {enabled: onClient() && !!customerId && customer?.customerType === 'registered'}
    )

    useEffect(() => {
        if (!isOrdersLoading && gcRef) {
            window.lastOrderDate = orders?.[0]?.creationDate || 'n/a'
            gcRef.current?.setAttribute('data-last-order-date', orders?.[0]?.creationDate || 'n/a')
        }
    }, [isOrdersLoading, gcRef])

    useEffect(() => {
        if (!isWishlistLoading && gcRef) {
            gcRef.current?.setAttribute(
                'data-wishlist-length',
                wishlist?.customerProductListItems?.length || 0
            )
        }
    }, [isWishlistLoading, gcRef])

    useEffect(() => {
        if (!isBasketLoading && gcRef) {
            gcRef.current?.setAttribute(
                'data-basket-items-length',
                basket?.productItems?.length ? JSON.stringify(basket?.productItems) : '[]'
            )
            gcRef.current?.setAttribute(
                'data-basket-gift-length',
                basket?.giftCertificateItems?.length
                    ? JSON.stringify(basket?.giftCertificateItems)
                    : '[]'
            )
        }
    }, [isBasketLoading, gcRef, basket])

    useEffect(() => {
        if (
            (customer?.customerType === 'registered' && customer?.email) ||
            customer?.customerType === 'guest'
        ) {
            gcRef.current?.setAttribute(
                'data-is-registered',
                customer?.customerType === 'registered'
            )
            window.birthdayMonth = customer?.c_birthdayMonth
            window.monetatePhilantropyShopper = customer?.c_monetatePhilantropyShopper
        }
    }, [customer?.customerType, customer?.email, gcRef])

    const triggerMonetateEvent = () => {
        let breadCrumb = 'Home/' + product.name
        if (breadCrumb) {
            window.mtBreadcrumb = breadCrumb.split('/')
            monetate.monetateTrackPage(
                'product',
                breadCrumb.split('/'),
                gcRef.current?.getAttribute('data-basket-items-length')
                    ? JSON.parse(gcRef.current?.getAttribute('data-basket-items-length'))
                    : [],
                gcRef.current?.getAttribute('data-is-registered') === 'true',
                gcRef.current?.getAttribute('data-wishlist-length') || 0,
                [product],
                null,
                null,
                gcRef.current?.getAttribute('data-basket-gift-length')
                    ? JSON.parse(gcRef.current?.getAttribute('data-basket-gift-length'))
                    : []
            )
            window.monetateRef = product?.id
        }
    }

    useEffect(() => {
        if (product?.id) {
            /** NOTE: Not a ReactJS practice, but we need to make sure that the order and the wishlist data is completely fetched and new product is available. **/
            var CONTROL_INTERVAL_MODULE = setInterval(function () {
                if (
                    (gcRef.current?.getAttribute('data-last-order-date') ||
                        customer?.customerType !== 'registered') &&
                    (gcRef.current?.getAttribute('data-wishlist-length') ||
                        customer?.customerType !== 'registered') &&
                    gcRef.current?.getAttribute('data-basket-items-length') !== '' &&
                    gcRef.current?.getAttribute('data-is-registered')
                ) {
                    triggerMonetateEvent()
                    clearInterval(CONTROL_INTERVAL_MODULE)
                }
            }, 1000) // check for every 1s
        }
    }, [product?.id])

    const totalQuantity = getTotalQuantity(baskets?.baskets?.[0] || baskets, changeUpProductID)

    useEffect(() => {
        const city = cookie?.get('city')
        const country = cookie?.get('countryCode')
        const state = cookie?.get('state')
        if (!country) return

        if (
            ((customer?.customerType === 'registered' && customer?.email) ||
                customer?.customerType === 'guest') &&
            !isLoading &&
            !globalDataLoaded
        ) {
            if (debounceRef.current) clearTimeout(debounceRef.current)

            debounceRef.current = setTimeout(() => {
                tagManager.globalDataLoad(
                    'PDP',
                    'other',
                    totalQuantity,
                    baskets?.baskets?.[0]?.productTotal,
                    customer,
                    city,
                    country,
                    state
                )
                setGlobalDataLayerLoaded(true)
            }, 2000)
        }

        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current)
        }
    }, [
        customer?.customerType,
        customer?.email,
        isLoading,
        cookie,
        globalDataLoaded,
        totalQuantity,
        baskets
    ])

    useEffect(() => {
        const city = cookie.get('city')
        const country = cookie.get('countryCode') || 'US'
        const state = cookie.get('state')
        if (
            !dataLayerLoaded &&
            product?.name &&
            globalDataLoaded &&
            ((customer?.customerType === 'registered' && customer?.email) ||
                customer?.customerType === 'guest')
        ) {
            try {
                tagManager.dataLayerLoad(
                    'PDP',
                    isRegistered,
                    '',
                    countryISO,
                    currencyCode,
                    customer,
                    city,
                    country,
                    state,
                    null,
                    null,
                    'other'
                )
            } catch (e) {
                tagManager.dataLayerLoad(
                    'PDP',
                    isRegistered,
                    'breadCrumb',
                    null,
                    null,
                    customer,
                    city,
                    country,
                    state,
                    null,
                    null,
                    'other'
                )
            }
            setDataLayerLoaded(true)
        }
    }, [product?.name, globalDataLoaded, customer, isRegistered, countryISO, currencyCode])

    useEffect(() => {
        if (dataLayerLoaded) {
            window.__dataLayerLoaded = true
            tagManager.productView(product, null, false, false, true)
            window.__dataLayerLoaded = false
        }
    }, [product?.id, dataLayerLoaded, selectedAmount])

    const pageUrl = onClient()
        ? `${window.location.origin}${location.pathname}`
        : location?.pathname
    const giftCardSchema = getGiftCardSchema(product, selectedAmount, currencyCode, pageUrl)

    return (
        <Box
            className="sf-gift-card-page"
            layerStyle="fullPage"
            {...styles.page}
            ref={gcRef}
            data-wishlist-length=""
            data-last-order-date=""
            data-basket-items-length=""
            data-basket-gift-length=""
            data-is-registered=""
        >
            <Helmet>
                <title>
                    {product?.c_productTitle ||
                        intl.formatMessage({
                            id: 'gc.pdp.title',
                            defaultMessage: 'Gift Certificate | Kendra Scott Online Gift Card'
                        })}
                </title>
                <meta
                    name="description"
                    content={
                        product?.c_productDescription ||
                        intl.formatMessage({
                            id: 'gc.pdp.description',
                            defaultMessage:
                                'Buy a gift card at www.kendrascott.com. Always the perfect gift. No matter her taste, this is a one-size-fits-all gift.'
                        })
                    }
                />
                {product?.id && (
                    <script type="application/ld+json">{JSON.stringify(giftCardSchema)}</script>
                )}
            </Helmet>

            {(showAddToCartLoader || isGcLoading) && <LoadingSpinner position="fixed" />}

            {/* MOBILE HEADER */}
            <Box display={{base: 'block', lg: 'none'}}>
                <Box display="inline-flex" justifyContent="space-between" width="100%">
                    <CardTitle />
                    <CardsPriceRange amounts={configs.amounts} />
                </Box>

                {product && (
                    <Box mt="10px">
                        <CardSelectedDesign product={product} />
                    </Box>
                )}

                <ThumbnailImages
                    ids={productIds}
                    selectedId={selectedId}
                    setSelectedId={setSelectedId}
                    setFetchProduct={() => setFetchProduct(true)}
                    eGiftCardsJson={configs.products}
                />
            </Box>

            <Flex {...styles.flex}>
                <CardImage
                    selectedId={selectedId}
                    eGiftCardsJson={configs.products}
                    isGiftCert={true}
                />

                <Box width={{base: '100%', lg: '380px'}} mb="60px">
                    <Box mb="30px">
                        <Box display={{base: 'none', lg: 'block'}}>
                            <CardTitle />
                            {product && (
                                <Box mt="14.5px">
                                    <CardsPriceRange amounts={configs.amounts} />
                                    <CardSelectedDesign product={product} />
                                </Box>
                            )}
                        </Box>

                        <Box display={{base: 'none', lg: 'block'}}>
                            <ThumbnailImages
                                ids={productIds}
                                selectedId={selectedId}
                                setSelectedId={setSelectedId}
                                setFetchProduct={() => setFetchProduct(true)}
                                eGiftCardsJson={configs.products}
                            />
                        </Box>

                        <CardSelectedAmount selectedAmount={selectedAmount} />

                        <CardAmountSwatches
                            selectedAmount={selectedAmount}
                            amounts={configs.amounts}
                            setSelectedAmount={setSelectedAmount}
                        />
                    </Box>

                    <CardForm
                        submitForm={submitForm}
                        product={product}
                        showAddToCartLoader={showAddToCartLoader}
                        showAddedCta={showAddedCta}
                        form={form}
                    />

                    <CardCheckBalance />

                    <Box mt="30px" sx={{'#pro-reviews-tab': {display: 'none'}}}>
                        {product?.c_pdpAccordionConfig ? (
                            isHydrated() ? (
                                <ProductDetailsAccordion product={product} />
                            ) : (
                                <>
                                    <Skeleton padding="20px 0" height="31px" />
                                    <Skeleton padding="20px 0" height="31px" />
                                    <Skeleton padding="20px 0" height="31px" />
                                    <Skeleton padding="20px 0" height="31px" />
                                </>
                            )
                        ) : null}
                    </Box>
                </Box>
            </Flex>

            <Box className="optiversal-link-wrapper">
                {product?.id && enabledRelatedLinksPDP && (
                    <OptiversalLinks query={product?.id} maxLinks={maxLinksPDP} queryType="sku" />
                )}
            </Box>
        </Box>
    )
}

GiftCard.getTemplateName = () => 'gift-card'

GiftCard.propTypes = {
    match: PropTypes.object
}

export default GiftCard
